/**
 * Forum Board Page - Store
 * 
 * Modern board page with topic cards and filters
 */

import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useStore } from '@/contexts/StoreContext'
import { useTheme } from '@/contexts/ThemeContext'
import forumStoreService, {
  type ForumBoard,
  type ForumTopic,
} from '@/lib/services/forumStore.service'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Plus,
  ArrowLeft,
  Filter,
  MessageCircle,
  TrendingUp,
  Clock,
  Sparkles,
  Lock,
} from 'lucide-react'
import PageHero from '@/components/common/PageHero'
import ForumTopicCard from '@/components/forum/ForumTopicCard'
import { useTranslation } from 'react-i18next';

import { Caixa } from '@/construtor/caixa';
export default function ForumBoardPage() {
  const { t } = useTranslation();
  const { store } = useStore()
  const { config } = useTheme()
  const { categorySlug, boardSlug } = useParams<{
    categorySlug: string
    boardSlug: string
  }>()
  const navigate = useNavigate()

  const [board, setBoard] = useState<ForumBoard | null>(null)
  const [topics, setTopics] = useState<ForumTopic[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [filter, setFilter] = useState<'all' | 'pinned' | 'popular' | 'new' | 'locked'>('all')

  useEffect(() => {
    if (categorySlug && boardSlug && store?.id) {
      loadBoard()
    }
  }, [categorySlug, boardSlug, store])

  useEffect(() => {
    if (board) {
      loadTopics()
    }
  }, [board, page])

  async function loadBoard() {
    if (!store?.id) return
    
    try {
      const data = await forumStoreService.getBoardBySlug(
        store.id,
        categorySlug!,
        boardSlug!,
      )
      setBoard(data)
    } catch (error) {
      console.error('Erro ao carregar board:', error)
    }
  }

  async function loadTopics() {
    try {
      setLoading(true)
      const data = await forumStoreService.listTopics(store!.id, board!.id, page)
      setTopics(data.topics)
      setTotal(data.total)
    } catch (error) {
      console.error('Erro ao carregar tópicos:', error)
    } finally {
      setLoading(false)
    }
  }

  // Filter topics based on selected filter
  const filteredTopics = useMemo(() => {
    const now = new Date()
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

    switch (filter) {
      case 'pinned':
        return topics.filter(topic => topic.isPinned)
      
      case 'popular':
        // Sort all topics by popularity score (views + posts + reactions)
        return [...topics].sort((a, b) => {
          const scoreA = a.views + (a.postsCount * 5) + (a.reactionsCount * 2)
          const scoreB = b.views + (b.postsCount * 5) + (b.reactionsCount * 2)
          return scoreB - scoreA // Higher score first
        })
      
      case 'new':
        // Topics created in last 7 days, sorted by creation date
        return [...topics]
          .filter(topic => new Date(topic.createdAt) > sevenDaysAgo)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      
      case 'locked':
        return topics.filter(topic => topic.isLocked)
      
      case 'all':
      default:
        return topics
    }
  }, [topics, filter])

  if (!board) {
    return (
      <div>
        <PageHero
          title="Fórum"
          breadcrumbs={[
            { label: 'Fórum', href: '/forum' },
            { label: t('store.forum.loading') },
          ]}

        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Hero */}
      <PageHero
        title={`${board.icon} ${board.name}`}
        breadcrumbs={[
          { label: 'Fórum', href: '/forum' },
          { label: board.name },
        ]}

      />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 -mt-24 pt-24 relative z-10">


        {/* Board Header */}
        <Caixa id="forum.quadro.cabecalho" miolo>
        <Card
          className="mb-8 border-card-border overflow-hidden shadow-glow"
          style={{ borderLeft: `6px solid ${board.color}` }}
        >
          <CardContent className="p-8 relative">
            <div
              className="absolute inset-0 opacity-5 pointer-events-none"
              style={{
                background: `linear-gradient(135deg, ${board.color}40 0%, transparent 100%)`,
              }}
            />
            <div className="flex items-start justify-between gap-6 relative z-10">
              <div className="flex items-start gap-6 flex-1">
                {/* Board Icon */}
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl flex-shrink-0 shadow-lg"
                  style={{
                    backgroundColor: board.color + '20',
                    border: `3px solid ${board.color}40`,
                  }}
                >
                  {board.icon}
                </div>

                {/* Board Info */}
                <div className="flex-1">
                  <h1 className="text-4xl font-bold mb-3 text-foreground flex items-center gap-3">
                    {board.name}
                    {!board.isLocked && (
                      <Sparkles className="w-6 h-6 text-primary animate-pulse" />
                    )}
                  </h1>
                  <p className="text-muted-foreground text-lg mb-4">
                    {board.description}
                  </p>

                  {/* Board Stats */}
                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
                      <MessageCircle className="w-5 h-5 text-primary" />
                      <div>
                        <div className="text-lg font-bold text-foreground">
                          {board.topicsCount}
                        </div>
                        <div className="text-xs text-muted-foreground">{t('store.forum.topics')}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-accent" />
                      <div>
                        <div className="text-lg font-bold text-foreground">
                          {board.postsCount}
                        </div>
                        <div className="text-xs text-muted-foreground">{t('store.forum.posts')}</div>
                      </div>
                    </div>

                    {board.lastPost && (
                      /*
                        Âmbar aqui não significava nada: "último post" é
                        informação neutra, não alerta. Cor de estado fica
                        reservada para estado.
                      */
                      <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
                        <Clock className="w-5 h-5 text-primary" />
                        <div>
                          <div className="text-xs text-muted-foreground">{t('store.forum.lastPostBy')}</div>
                          <div className="text-sm font-bold text-foreground">
                            {board.lastPost.authorName}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* New Topic Button */}
              {!board.isLocked && (
                <Caixa id="forum.quadro.novo">
                <Button
                  onClick={() =>
                    navigate(`/forum/${categorySlug}/${boardSlug}/new-topic`)
                  }
                  size="lg"
                  className="flex-shrink-0 shadow-lg hover:shadow-glow transition-all"
                >
                  <Plus className="w-5 h-5" />
                  {t('store.forum.newTopic')}
                </Button>
                </Caixa>
              )}
            </div>
          </CardContent>
        </Card>
        </Caixa>

        {/* Filter Bar */}
        <Caixa id="forum.quadro.filtros">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-muted-foreground" />
            <div className="flex gap-2 flex-wrap">
              <Badge
                variant={filter === 'all' ? 'secondary' : 'outline'}
                className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-all"
                onClick={() => setFilter('all')}
              >
                {t('store.forum.filterAll')}
              </Badge>
              <Badge
                variant={filter === 'pinned' ? 'secondary' : 'outline'}
                className="cursor-pointer hover:bg-primary/10 hover:border-primary transition-all"
                onClick={() => setFilter('pinned')}
              >
                {t('store.forum.filterPinned')}
              </Badge>
              <Badge
                variant={filter === 'popular' ? 'secondary' : 'outline'}
                className="cursor-pointer hover:bg-primary/10 hover:border-primary transition-all"
                onClick={() => setFilter('popular')}
              >
                {t('store.forum.filterPopular')}
              </Badge>
              <Badge
                variant={filter === 'new' ? 'secondary' : 'outline'}
                className="cursor-pointer hover:bg-primary/10 hover:border-primary transition-all"
                onClick={() => setFilter('new')}
              >
                {t('store.forum.filterNew')}
              </Badge>
              <Badge
                variant={filter === 'locked' ? 'secondary' : 'outline'}
                className="cursor-pointer hover:bg-primary/10 hover:border-primary transition-all"
                onClick={() => setFilter('locked')}
              >
                <Lock className="w-3 h-3 mr-1" />
                {t('store.forum.filterLocked')}
              </Badge>
            </div>
          </div>

          <div className="text-sm text-muted-foreground">
            {filteredTopics.length} {filteredTopics.length === 1 ? 'tópico' : 'tópicos'}
          </div>
        </div>
        </Caixa>

        {/* Topics List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-xl animate-pulse"></div>
            </div>
          </div>
        ) : filteredTopics.length === 0 ? (
          <Caixa id="forum.quadro.vazio" miolo>
          <Card className="border-card-border">
            <CardContent className="p-12">
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <MessageCircle className="w-10 h-10 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {filter === 'all' || filter === 'popular'
                      ? t('store.forum.emptyTopicsTitle')
                      : t(
                          filter === 'pinned'
                            ? 'store.forum.emptyTopicsPinned'
                            : filter === 'new'
                              ? 'store.forum.emptyTopicsNew'
                              : 'store.forum.emptyTopicsLocked',
                        )}
                  </h3>
                  <p className="text-muted-foreground text-lg mb-6">
                    {t('store.forum.emptyTopicsBody')}
                  </p>
                </div>
                {!board.isLocked && (
                  <Button
                    onClick={() =>
                      navigate(`/forum/${categorySlug}/${boardSlug}/new-topic`)
                    }
                    size="lg"
                    className="shadow-lg hover:shadow-glow transition-all"
                  >
                    <Plus className="w-5 h-5" />
                    {t('store.forum.createFirstTopic')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          </Caixa>
        ) : (
          <Caixa id="forum.quadro.topicos">
          <div>
            {filteredTopics.map((topic) => (
              <Caixa id="forum.quadro.topico" repetida envolver key={topic.id}>
              <ForumTopicCard
                key={topic.id}
                topic={topic}
                categorySlug={categorySlug!}
                boardSlug={boardSlug!}
              />
              </Caixa>
            ))}
          </div>
          </Caixa>
        )}

        {/* Pagination */}
        {total > 20 && (
          <Caixa id="forum.quadro.paginas">
          <div className="mt-8 flex justify-center items-center gap-3">
            {page > 1 && (
              <Button
                variant="outline"
                onClick={() => setPage(page - 1)}
                className="hover:bg-primary/10 hover:border-primary transition-all"
              >
                ← Anterior
              </Button>
            )}
            <div className="flex items-center gap-2">
              <span className="px-6 py-2 bg-card border border-border rounded-lg text-sm text-foreground font-medium">
                Página <span className="text-primary font-bold">{page}</span> de{' '}
                {Math.ceil(total / 20)}
              </span>
            </div>
            {page < Math.ceil(total / 20) && (
              <Button
                variant="outline"
                onClick={() => setPage(page + 1)}
                className="hover:bg-primary/10 hover:border-primary transition-all"
              >
                Próxima →
              </Button>
            )}
          </div>
          </Caixa>
        )}
      </div>
    </div>
  )
}
